<template>
  <div class="cmp-table-sortable @container">
    <div :class="wrapperClass">
      <div
        class="flex md:items-center gap-4 mt-5 grow"
        :class="{
          'flex-col-reverse md:flex-row': !reverseFilterFlexOrder,
          'flex-col md:flex-row': reverseFilterFlexOrder,
          [filterRowClasses]: filterRowClasses,
        }"
      >
        <!-- FILTERS DRAWER-->
        <div
          class="filters-drawer flex-col md:gap-4 grow pb-4"
          :class="{
            'hidden md:flex md:flex-row':
              hideFilterBarOnMobile || ($slots.filters && !filtersShown),
            'flex md:flex-row': !hideFilterBarOnMobile && (!$slots.filters || filtersShown),
          }"
        >
          <slot name="title" />
          <slot
            name="filters"
            :dictionary-rows="dictionaryRows"
            :toggle-dictionary="onToggleDictionary"
          >
          </slot>
          <FiltersYearRadio
            v-if="year !== undefined"
            class="md:ml-auto md:hidden"
            :class="{ hidden: hideFilterBarOnMobile }"
            v-model="year"
            :max-year="maxYear"
            :min-year="minYear"
          />
        </div>

        <!-- FILTERS ROW -->
        <div
          class="flex gap-3"
          :class="{
            'md:hidden': !dictionaryRows?.length,
            [filterRowInnerClasses]: filterRowInnerClasses,
          }"
        >
          <!-- MOBILE ONLY -- CLOSE FILTERS -->
          <UButton
            v-if="$slots.filters"
            class="flex md:hidden justify-center lg:ml-auto grow text-sm"
            color="black"
            size="xl"
            @click="onToggleFilter"
          >
            <template v-if="filtersShown">
              <i class="far fa-times inline-block"></i>
              Close <span class="hidden md:inline">Filters</span>
            </template>
            <template v-else>
              <i class="far fa-filter inline-block"></i>
              Filters
              <template v-if="numBasicFilterssActive">
                <span class="inline-block text-sm">({{ numBasicFilterssActive }})</span>
              </template>
            </template>
          </UButton>
          <UTooltip v-if="$slots.filters" :text="'Reset all filters'" :popper="{ arrow: true }">
            <UButton
              class="flex justify-center md:ml-auto grow btn-64-reset"
              color="white"
              size="xl"
              @click="onResetFilters"
            >
              <i class="fas fa-sync-alt inline-block"></i>
              <span class="hidden">Reset</span>
            </UButton>
          </UTooltip>
          <ButtonLegend
            class="grow md:grow-0 md:ml-auto"
            v-if="dictionaryRows?.length"
            @click="isOpen = true"
          />
          <UTooltip
            v-if="activeFiltersCount !== undefined && restricted"
            :text="'Sign up for an account today to access Advanced Filters'"
            :popper="{ arrow: true }"
          >
            <ButtonAdvancedFilters
              class="grow md:grow-0 md:ml-auto"
              :restricted="restricted"
              :active-filters-count="activeFiltersCount"
              @click="onToggleAdvancedFilters"
            />
          </UTooltip>
          <ButtonAdvancedFilters
            class="grow md:grow-0 md:ml-auto"
            :restricted="restricted"
            v-else-if="activeFiltersCount !== undefined && !restricted"
            :active-filters-count="activeFiltersCount"
            @click="onToggleAdvancedFilters"
          />
        </div>
      </div>

      <template v-if="!restricted && $slots['filter-badges']">
        <div class="my-3">
          <slot name="filter-badges" />
        </div>
      </template>

      <!-- OVERVIEW TEXT, PAGINATION, YEAR FILTER -->
      <div
        class="flex flex-col gap-2 @[87.5rem]:flex-row py-3.5 border-b border-gray-200 dark:border-gray-700 @[87.5rem]:items-center justify-between"
      >
        <div class="flex flex-col gap-5 sm:flex-row">
          <p class="" v-if="!restricted && total && total > 1">
            Found {{ formatNumber(total || 0) }} {{ plural || 'Results' }}
            <template v-if="pageCount || 0 > 1"
              >. Viewing page {{ page }} of {{ formatNumber(Math.ceil(pageCount || 0)) }}</template
            >
          </p>
          <p v-else-if="total == 1">Found 1 Result. Viewing page 1 of 1</p>
          <template v-if="model && model.length">
            <p class="md:hidden">{{ model.length }} Players Selected</p>
          </template>
        </div>
        <div
          class="@[87.5rem]:ml-auto flex gap-5 flex-col md:flex-row md:gap-5 md:items-center md:justify-between"
          :class="{
            '': total !== undefined && perPage !== undefined && total > perPage,
          }"
        >
          <FiltersYearRadio
            v-if="year !== undefined"
            class="@[87.5rem]:ml-auto hidden md:flex"
            v-model="year"
            :max-year="maxYear"
            :min-year="minYear"
          />
          <div
            v-if="selectedColumns !== undefined || defaultColumns !== undefined"
            class="flex gap-2 items-center mt-3 md:mt-0"
          >
            <span class="inline-block text-sm">Columns</span>
            <div class="flex lg:mr-3">
              <USelectMenu
                class="relative"
                v-model="localSelectedColumns"
                :options="defaultColumns"
                multiple
                placeholder="Fields"
                @change="onSelectedColumnsChange"
              />
              <UButton v-if="selectedColumns !== undefined" color="white" @click="onClearColumns">
                <i class="fas fa-times"></i>
                Clear
              </UButton>
            </div>
          </div>
          <TablePagination
            class="md:ml-auto hidden lg:flex"
            v-if="
              (page && page > 1) ||
              (total !== undefined && perPage !== undefined && total > perPage)
            "
            v-model:page="page"
            v-model:per-page="perPage"
            v-model:total="total"
          />
        </div>
      </div>
      <div
        class="relative overflow-x-auto custom-scrollbar horizontal bg-white dark:bg-neutral-950"
      >
        <template v-if="restricted">
          <div
            class="absolute top-[50%] w-full flex flex-col items-center justify-center gap-4 z-[4] left-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <HeadingBasic class="text-center" :size="3">
              <template v-if="requiresPortalPass">
                Subscribe to the 2025 Transfer Portal Pass for access
              </template>
              <template v-else-if="hasTrialed"> Enjoy your Free Trial? </template>
              <template v-else
                >Sign Up for a Free Trial for Full Access to 64Analytics.com</template
              >
            </HeadingBasic>
            <UButton
              v-if="restricted"
              :ui="{ rounded: 'rounded-full' }"
              size="xl"
              @click="navigateTo('/sign-up')"
              class="!bg-cardinal !text-white block px-8 w-full md:w-auto rounded-md md:rounded-full"
            >
              <template v-if="requiresPortalPass">Get the Portal Pass</template>
              <template v-else-if="hasTrialed">Activate Your Subscription</template>
              <template v-else>Start a Free Trial</template>
            </UButton>
          </div>
        </template>
        <UTable
          v-model="model"
          :class="tableClass"
          :columns="columns"
          :rows="rows"
          sortAscIcon=""
          sortDescIcon=""
          :sort-button="{
            leadingIcon: '',
            icon: '',
            activeClass: 'bg-cardinal',
            // inactiveClass: 'bg-neutral-950',
            exactActiveClass: 'bg-cardinal-300',
          }"
          v-model:sort="sort"
          :loading="loading"
          :max="1"
          sort-mode="manual"
          @update:sort="$emit('refresh', sort)"
          :ui="{
            wrapper: 'relative custom-scrollbar horizontal overflow-x-auto',
            tr: {
              base: `even:bg-neutral-100 dark:bg-neutral-900 dark:even:bg-neutral-950`,
            },
          }"
        >
          <template #loading-state>
            <slot v-if="$slots.loadingState" name="loadingState"></slot>
          </template>
          <template #index-data="{ row }">
            <slot name="index-data" :row="row"></slot>
          </template>
          <template #year-data="{ row }">
            <slot name="year-data" :row="row"></slot>
          </template>
          <template #player_name-data="{ row }">
            <slot name="player_name-data" :row="row"></slot>
          </template>
          <template #team_name-data="{ row }">
            <slot name="team_name-data" :row="row"></slot>
          </template>
          <template #team_id-data="{ row }">
            <slot name="team_id-data" :row="row"></slot>
          </template>
          <template #initiated_date-data="{ row }">
            <slot name="initiated_date-data" :row="row"></slot>
          </template>
          <template #old_team-data="{ row }">
            <slot name="old_team-data" :row="row"></slot>
          </template>
          <template #previous_team_name-data="{ row }">
            <slot name="previous_team_name-data" :row="row"></slot>
          </template>
          <template #committed_team_name-data="{ row }">
            <slot name="committed_team_name-data" :row="row"></slot>
          </template>
          <template #new_team_name-data="{ row }">
            <slot name="new_team_name-data" :row="row"></slot>
          </template>
          <template #committed_team-data="{ row }">
            <slot name="committed_team-data" :row="row"></slot>
          </template>
          <template #drafted-data="{ row }">
            <slot name="drafted-data" :row="row"></slot>
          </template>
          <template #stats-data="{ row }">
            <slot name="stats-data" :row="row"></slot>
          </template>
          <template #sixty_four_rank_weighted_run_efficiency-data="{ row }">
            <slot name="sixty_four_rank_weighted_run_efficiency-data" :row="row"></slot>
          </template>
          <template #total_wins-data="{ row }">
            <slot name="total_wins-data" :row="row"></slot>
          </template>
          <template #rank_weighted_run_created_per_35_at_bat-data="{ row }">
            <slot name="rank_weighted_run_created_per_35_at_bat-data" :row="row"></slot>
          </template>
          <template #rank_weighted_run_allowed_per_35_at_bat-data="{ row }">
            <slot name="rank_weighted_run_allowed_per_35_at_bat-data" :row="row"></slot>
          </template>
          <template #conference_abbreviation-data="{ row }">
            <slot name="conference_abbreviation-data" :row="row"></slot>
          </template>
          <template #pick_differential-data="{ row }">
            <slot name="pick_differential-data" :row="row"></slot>
          </template>
          <template #signing_bonus-data="{ row }">
            <slot name="signing_bonus-data" :row="row"></slot>
          </template>
          <template #transferred-data="{ row }">
            <slot name="transferred-data" :row="row"></slot>
          </template>
          <template #integer_64_rank_total-data="{ row }">
            <slot name="integer_64_rank_total-data" :row="row"></slot>
          </template>
          <template #current_rpi-data="{ row }">
            <slot name="current_rpi-data" :row="row"></slot>
          </template>
          <template #classification-data="{ row }">
            <slot name="classification-data" :row="row"></slot>
          </template>
          <template #in_portal-data="{ row }">
            <slot name="in_portal-data" :row="row"></slot>
          </template>
          <template #note_text-data="{ row }">
            <slot name="note_text-data" :row="row"> </slot>
          </template>
          <template #created_at-data="{ row }">
            <slot name="created_at-data" :row="row"> </slot>
          </template>
          <template #height-data="{ row }">
            <slot name="height-data" :row="row">
              {{ row.height ? inchesToFeetAndInches(row.height) : '--' }}
            </slot>
          </template>
          <template #throw-data="{ row }">
            <slot name="throw-data" :row="row">
              {{ row.throw ?? '--' }}
            </slot>
          </template>
          <template #bat-data="{ row }">
            <slot name="bat-data" :row="row">
              {{ row.bat ?? '--' }}
            </slot>
          </template>
          <template #sum_run_expectancy_change-data="{ row }">
            <slot name="sum_run_expectancy_change-data" :row="row"></slot>
          </template>
          <template #hometown-data="{ row }">
            <slot name="hometown-data" :row="row"></slot>
          </template>
          <template #actions-data="{ row }">
            <slot name="actions-data" :row="row"> </slot>
          </template>
        </UTable>
      </div>
      <div v-if="total !== undefined && perPage !== undefined && total > perPage" class="mt-3 mb-6">
        <TablePagination v-model:page="page" v-model:per-page="perPage" v-model:total="total" />
      </div>
    </div>
    <USlideover v-model="isOpen" v-if="dictionaryRows?.length">
      <UCard class="mt-5 overflow-y-auto">
        <div class="flex items-center justify-between mb-8">
          <HeadingBasic :size="3">Legend</HeadingBasic>
          <p class="ml-3">Press <UKbd>Esc</UKbd> to close</p>
          <UButton color="white" size="xs" @click="isOpen = false" class="ml-auto">
            <i class="far fa-times mr-1"></i>
            Close
          </UButton>
        </div>
        <div class="flex flex-col gap-2">
          <p
            v-for="row in dictionaryRows"
            class="border-b border-b-neutral-200 dark:border-b-neutral-700 pb-3 text-sm mb-1 flex gap-2 leading-tight"
          >
            <strong class="flex-[0_0_100px] block">{{ row.abbreviation }}</strong>
            {{ row.definition }}
          </p>
        </div>
      </UCard>
    </USlideover>
  </div>
</template>

<script lang="ts" setup generic="T">
import { useVModels } from '@vueuse/core';
import type { DictionaryRow } from '~/composables/useDataDictionary';
import { formatNumber } from '~/lib/helpers/format-number';
import type { Column } from '~/types/data-dictionary';
import { inchesToFeetAndInches } from '~/utils/view-helpers';
const store = useProfileStore();
const { hasActiveSubscription, hasTrialed } = storeToRefs(store);

// const selectedColumns = defineModel('selectedColumns', { type: Array as PropType<Column[]> });
const model = defineModel();

const props = withDefaults(
  defineProps<{
    filtersShown?: boolean;
    columns: Column[];
    defaultColumns?: Column[];
    rows?: T[];
    page?: number;
    perPage?: number;
    pageCount?: number;
    total?: number;
    year?: number | string;
    minYear?: number | null;
    maxYear?: number | null;
    loading?: boolean;
    plural?: string;
    restricted?: boolean;
    sort?: { column: string; direction: 'asc' | 'desc' };
    dictionary?: DictionaryRow[];
    reverseFilterFlexOrder?: boolean;
    activeFiltersCount?: number;
    selectedColumns: Column[];
    hideFilterBarOnMobile?: boolean;
    numBasicFilterssActive?: number;
    // for the board
    requiresPortalPass?: boolean;
    filterRowClasses?: string;
    filterRowInnerClasses?: string;
  }>(),
  {
    filtersShown: false,
    reverseFilterFlexOrder: false,
    restricted: false,
    maxYear: 2026,
    minYear: 2021,
    requiresPortalPass: false,
    hideFilterBarOnMobile: false,
    numBasicFilterssActive: 0,
    filterRowClasses: '',
    filterRowInnerClasses: '',
  }
);
const emit = defineEmits([
  'update:sort',
  'refresh',
  'update:year',
  'reset-filters',
  'update:page',
  'update:per-page',
  'toggle-advanced-filters',
  'toggle-basic-filters',
  'selected-columns-update',
  'click:actions',
  'update:filtersShown',
]);
const {
  sort,
  page,
  perPage,
  total,
  pageCount,
  loading,
  dictionary,
  year,
  filtersShown,
  // Reactivity issue? Try closing dev tools.... without this being useVModeled, reactivity was lost aftr refreshing the page... restricted was not reactive and stuck around even when set to false
  restricted,
} = useVModels(props, emit);

const wrapperClass = computed(() => {
  return {
    // Reactivity issue? Try closing dev tools.... without this being useVModeled, reactivity was lost aftr refreshing the page... restricted was not reactive and stuck around even when set to false
    restricted: restricted.value,
  };
});

watch(
  () => props.restricted,
  () => {
    console.log('restricted changed', props.restricted);
  }
);

const localSelectedColumns = ref([...(props.selectedColumns || [])]);

// when they change from the outside, reflect that here
watch(
  () => props.selectedColumns,
  (value) => {
    localSelectedColumns.value = value;
  }
);

const onSelectedColumnsChange = () => {
  emit('selected-columns-update', localSelectedColumns.value);
};

// const selectedColumns = ref([...columns.value]);
const isOpen = ref(false);
const onToggleFilter = () => {
  filtersShown.value = !filtersShown.value;
  emit('toggle-basic-filters', filtersShown.value);
};
const onToggleDictionary = () => {
  isOpen.value = !isOpen.value;
};
const dictionaryRows: ComputedRef<DictionaryRow[]> = computed(() => {
  // return (
  //   dictionary.value?.filter(
  //     (drow) => drow.definition && columns.value.find((col) => col.key == drow.characteristic)
  //   ) || []
  // );
  const rows: DictionaryRow[] = [];
  (props.defaultColumns ?? props.columns)?.map((column) => {
    const row = dictionary.value?.find((row) =>
      // column.key == row.characteristic && row.definition && row.definition.indexOf('see') !== 0
      {
        // console.log(column.key, row.characteristic, row.definition);
        // if (column.key == row.characteristic)
        //   console.log(row.table, row.characteristic, row.definition);
        return column.key == row.characteristic && row.definition;
      }
    );
    if (row) rows.push(row);
  });
  return rows;
});
const onResetFilters = () => {
  filtersShown.value = false;
  emit('reset-filters');
};
const onToggleAdvancedFilters = () => {
  // when portal pass is required, its expected that this will get emitted so it can toggle the upsell
  if (props.restricted) return;
  emit('toggle-advanced-filters');
};
const onClickActions = (row: any) => {
  emit('click:actions', row);
};

const tableClass = computed(() => ({
  ['sixtyfour-data-table z-[1]']: true,
}));

const onClearColumns = () => {
  localSelectedColumns.value = localSelectedColumns.value.filter((col) =>
    ['year', 'player_name', 'team_name', 'conference_name', 'division_name', 'team_id'].includes(
      col.key
    )
  );
  onSelectedColumnsChange();
};
</script>

<style lang="scss">
.cmp-table-sortable {
  .restricted {
    .sixtyfour-data-table {
      position: relative;
      &:before {
        content: '';
        display: block;
        height: 100%;
        width: 100%;
        background: white;
        position: absolute;
        opacity: 0.8;
        backdrop-filter: blur(40px);
        z-index: 3;
        @apply dark:bg-neutral-900 dark:opacity-90;
      }
      tbody {
        @apply relative;
        z-index: 2;
      }
    }
  }
}

.sixtyfour-data-table {
  overflow-x: scroll;
  tr {
    td,
    th {
      padding: 8px 12px;
      @apply text-neutral-900 dark:text-neutral-50;
    }
  }

  &::-webkit-scrollbar {
    width: 4px;
    border: 1px solid #d5d5d5;
  }

  &::-webkit-scrollbar-track {
    border-radius: 0;
    background: #eeeeee;
    @apply dark:bg-neutral-800;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 0;
    background: #b0b0b0;
    @apply bg-neutral-300 dark:bg-cardinal-900;
  }
  &::-webkit-scrollbar {
    height: 4px;
    width: 4px;
    border: 1px solid #d5d5d5;
  }

  thead {
    tr {
      .sticky-right {
        position: sticky;
        right: 0;
        background-color: !important;
        @apply bg-[#F5F5F5] dark:bg-[#2a2a2a];
      }
    }
  }

  tbody {
    tr {
      td.sticky-right {
        position: sticky;
        right: 0;
      }
      &:nth-child(odd) {
        td.sticky-right {
          position: sticky;
          right: 0;
          // background-color: #181818 !important;
          @apply bg-[white] dark:bg-[#181818];
        }
      }
      &:nth-child(even) {
        td.sticky-right {
          position: sticky;
          right: 0;
          // background-color: #0a0a0a !important;
          @apply bg-[#F3F3F3] dark:bg-[#0a0a0a];
        }
      }
    }
  }

  &.restricted {
    tbody {
      position: relative;
      &:before {
        content: '';
        display: block;
        height: 100%;
        width: 100%;
        background: white;
        position: absolute;
        opacity: 0.8;
        backdrop-filter: blur(40px);
        @apply dark:bg-neutral-900 dark:opacity-90;
      }
    }
  }
}

.filters-drawer {
  @apply lg:pb-0;
  > .formkit-outer {
    @apply lg:mb-0;
  }
}
</style>
